import type {
	MultipartAbortReason,
	MultipartLocalSizeMismatchDirection,
	MultipartReconciliationVerdict,
	MultipartSessionStatus,
} from '@atlas/types'
import axios from 'axios'
import { apiClient } from './api.client'
import type { ClassifyTuple } from './classification.service'
import type { FileDTO } from './file.service'

/** One presigned `UploadPart` URL and the window the backend guarantees for starting a request with it. */
export interface UploadPartPresign {
	partNumber: number
	uploadUrl: string
	/**
	 * A DURATION, never a timestamp: renewal compares it against locally measured
	 * elapsed time, so browser/server clock skew cannot affect expiry decisions.
	 */
	validForSeconds: number
}

export interface MultipartSessionResponse {
	multipartSessionId: string
	status: MultipartSessionStatus
	/** The backend-trusted size. Sizing, progress and part boundaries all come from here, never from the local file. */
	declaredFileSize: number
	partSize: number
	partCount: number
	uploadParts: UploadPartPresign[]
}

export interface ReconciledPart {
	partNumber: number
	size: number
}

export interface ReconciledInvalidPart extends ReconciledPart {
	expectedSize: number
}

export interface MultipartPartsResponse {
	multipartSessionId: string
	status: MultipartSessionStatus
	declaredFileSize: number
	partSize: number
	partCount: number
	verdict: MultipartReconciliationVerdict
	completedParts?: ReconciledPart[]
	missingPartNumbers?: number[]
	invalidParts?: ReconciledInvalidPart[]
	failureCode?: string | null
	finishingPollBudgetSeconds?: number
	/** Present once the session reached `CONFIRMED` — the same result a successful completion returns. */
	file?: FileDTO
}

/** `POST /complete` reached the confirmation boundary — the shared final result. */
export interface MultipartCompleteConfirmed {
	multipartSessionId: string
	status: MultipartSessionStatus.CONFIRMED
	file: FileDTO
}

/**
 * `POST /complete` found finalization genuinely still running, so the client
 * observes it with bounded `GET` polling against this budget rather than
 * calling completion again.
 */
export interface MultipartCompleteFinishing {
	multipartSessionId: string
	status: MultipartSessionStatus.COMPLETING | MultipartSessionStatus.COMPLETED
	finishingPollBudgetSeconds: number
}

/**
 * The two shapes `POST /complete` can answer with, discriminated by `status`.
 *
 * Branching on `status` rather than on the presence of `file` is what the union
 * enforces: no terminal state can reach either branch — each leaves as a coded
 * error — so a caller that sees neither `CONFIRMED` is looking at a session
 * that really is still finalizing.
 */
export type MultipartCompleteResponse = MultipartCompleteConfirmed | MultipartCompleteFinishing

export interface MultipartAbortResponse {
	multipartSessionId: string
	status: MultipartSessionStatus
}

/** Diagnostic context for an abort. It never changes what the abort does. */
export interface MultipartAbortDiagnostics {
	reason: MultipartAbortReason
	direction: MultipartLocalSizeMismatchDirection
}

/**
 * The multipart control plane. Bytes never pass through here except in
 * `uploadPart`, which goes straight to S3 with a presigned URL — Atlas sees
 * session state, never file content.
 */
export const fileMultipartService = {
	/**
	 * Creates a session for a fresh `uploadAttemptId`, idempotently recovers an
	 * existing one for that same attempt, or re-presigns parts of a known
	 * session. Exactly one identifier is sent; `classify` is accepted only on the
	 * attempt branch, and only the first time.
	 */
	createOrPresign: async (
		fileId: string,
		body: {
			uploadAttemptId?: string
			multipartSessionId?: string
			partNumbers: number[]
			classify?: ClassifyTuple[]
		},
	): Promise<MultipartSessionResponse> => {
		const response = await apiClient.post<MultipartSessionResponse>(`/files/${fileId}/multipart`, body)
		return response.data
	},

	/** Read-only reconciliation against live S3 state. Never mutates, never completes, never aborts. */
	getParts: async (fileId: string, sessionId: string): Promise<MultipartPartsResponse> => {
		const response = await apiClient.get<MultipartPartsResponse>(`/files/${fileId}/multipart/parts`, {
			params: { sessionId },
		})
		return response.data
	},

	/**
	 * Finalizes the session. The client sends no part numbers and no ETags:
	 * S3's own `ListParts` is authoritative, and Atlas reads it itself.
	 */
	complete: async (fileId: string, sessionId: string): Promise<MultipartCompleteResponse> => {
		const response = await apiClient.post<MultipartCompleteResponse>(`/files/${fileId}/multipart/complete`, {
			multipartSessionId: sessionId,
		})
		return response.data
	},

	abort: async (
		fileId: string,
		sessionId: string,
		diagnostics?: MultipartAbortDiagnostics,
	): Promise<MultipartAbortResponse> => {
		const response = await apiClient.delete<MultipartAbortResponse>(`/files/${fileId}/multipart`, {
			params: { sessionId, ...(diagnostics ?? {}) },
		})
		return response.data
	},

	/**
	 * One part, browser to S3. Raw axios, not `apiClient`: the presigned URL is
	 * the credential and S3 rejects an unexpected `Authorization` header.
	 *
	 * The returned ETag is diagnostic only. Atlas never accepts a client-supplied
	 * ETag, and a cross-origin response exposes the header only when the
	 * bucket's CORS configuration says so — so `null` here is normal and never
	 * blocks completion.
	 */
	uploadPart: async (
		uploadUrl: string,
		body: Blob,
		options?: { signal?: AbortSignal; onBytes?: (loaded: number) => void },
	): Promise<{ etag: string | null }> => {
		const response = await axios.put(uploadUrl, body, {
			signal: options?.signal,
			onUploadProgress: (event) => options?.onBytes?.(event.loaded),
		})
		const headers = response.headers as Record<string, unknown> | undefined
		const etag = headers?.etag ?? headers?.ETag
		return { etag: typeof etag === 'string' ? etag : null }
	},
}
